import Logger from 'utils/logging'
import client from '../client'

export interface DonateResponse {
    success: boolean
    message?: string
    errorCode?: string
    errors?: Record<string, string> // Field-specific validation errors
}

export interface DonateParams {
    plexAmount: number
    campaignId: string // This is Contentful Sys.Id that will be passed in for the service to look up the campaign
    characterId: number // The character ID of the character the user wants to donate from
}

export async function donatePlex(
    token: string,
    params: DonateParams
): Promise<DonateResponse> {
    try {
        const response = await client({
            url: '/plex/donate',
            headers: {
                Authorization: `Bearer ${token}`,
                Accept: 'application/json',
                'Access-Control-Allow-Origin': '*',
            },
            method: 'post',
            data: params,
        })

        // If we get a successful HTTP response, consider it a success
        if (response && response.status >= 200 && response.status < 300) {
            return {
                success: true,
                ...response.data,
            }
        }

        // This should never happen (non-200 without throwing), but just in case
        return {
            success: false,
            message: response?.data?.message || 'Unexpected donation response',
            errorCode: response?.data?.errorCode || 'UnexpectedResponse',
            errors: response?.data?.errors || {},
        }
    } catch (error) {
        // Check if this is a standard API error response (e.g., validation error)
        if (error.response && error.response.status) {
            const status = error.response.status

            // Only log unexpected errors (5xx series or unexpected 4xx)
            if (
                status >= 500 ||
                ![400, 401, 403, 404, 409, 422].includes(status)
            ) {
                Logger.captureNetworkException(
                    error,
                    'user/plex/donate',
                    'user.plex.donate'
                )
            }

            // Return structured error from API
            if (error.response.data) {
                // Extract error code from response
                const errorCode =
                    error.response.data.errorCode ||
                    (error.response.data.data === false
                        ? 'SystemUnexpectedError'
                        : null)

                return {
                    success: false,
                    message: error.response.data.message || 'Donation failed',
                    errorCode,
                    errors: error.response.data.errors || {},
                }
            }
        }

        // For network errors or other unexpected issues, log and return generic error
        Logger.captureNetworkException(
            error,
            'user/plex/donate',
            'user.plex.donate'
        )

        return {
            success: false,
            message: error.message || 'An error occurred during donation',
            errors: {},
        }
    }
}
